import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { gray } from "../../../theme/themePrimitives";

interface PdfContentHeaderProps {
  title?: string;
  currentPage?: number;
  // Pass null/omit to hide the page count (page-by-page mode already
  // shows its own counter in the nav bar below).
  numPages?: number | null;
  // Search is only wired up in continuous-scroll mode; omit onSearchTermChange
  // to hide the search bar entirely.
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
  matchCount?: number;
  // 1-based index of the active match, 0 when there are no matches.
  activeMatchIndex?: number;
  onNextMatch?: () => void;
  onPrevMatch?: () => void;
  onClearSearch?: () => void;
}

export const PdfContentHeader: React.FC<PdfContentHeaderProps> = ({
  title,
  currentPage,
  numPages,
  searchTerm,
  onSearchTermChange,
  matchCount = 0,
  activeMatchIndex = 0,
  onNextMatch,
  onPrevMatch,
  onClearSearch,
}) => {
  const searchEnabled = onSearchTermChange != null;
  const hasQuery = Boolean(searchTerm);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) {
        onPrevMatch?.();
      } else {
        onNextMatch?.();
      }
    } else if (e.key === "Escape") {
      onClearSearch?.();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${gray[200]}`,
      }}
    >
      {searchEnabled && (
        <TextField
          size="small"
          placeholder="Search in document"
          value={searchTerm ?? ""}
          onChange={(e) => onSearchTermChange?.(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              height: 44,
              paddingRight: "5px",
              borderRadius: "12px",
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: gray[400] }} />
                </InputAdornment>
              ),
              endAdornment: hasQuery && (
                <InputAdornment position="end">
                  <Typography
                    variant="caption"
                    sx={{
                      color: gray[500],
                      whiteSpace: "nowrap",
                      mr: 1,
                      borderRight: `1px solid ${gray[300]}`,
                      pr: 1.5,
                    }}
                  >
                    {matchCount ? `${activeMatchIndex}/${matchCount}` : "0/0"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={onPrevMatch}
                    disabled={!matchCount}
                    aria-label="Previous match"
                    sx={{
                      border: "none",
                      ":hover": {
                        borderRadius: "50%",
                      },
                    }}
                  >
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      sx={{ color: gray[500] }}
                    />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={onNextMatch}
                    disabled={!matchCount}
                    aria-label="Next match"
                    sx={{
                      border: "none",
                      ":hover": {
                        borderRadius: "50%",
                      },
                    }}
                  >
                    <KeyboardArrowDownIcon
                      fontSize="small"
                      sx={{ color: gray[500] }}
                    />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={onClearSearch}
                    aria-label="Clear search"
                    sx={{
                      border: "none",
                      ":hover": {
                        borderRadius: "50%",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" sx={{ color: gray[500] }} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      )}
      {numPages != null && (
        <Typography variant="body2" color="text.secondary" noWrap>
          Page {currentPage ?? 1} of {numPages}
        </Typography>
      )}
    </Box>
  );
};
